/**
 * ObondoChatPanel — a real, inline chat composer for the Obondo Coach page.
 *
 * The page previously only showed a passive feed of Obondo's own proactive
 * pushes with no way to actually TALK to Obondo — the only interactive
 * chat lived under the separate Kimani FAB (audit finding, Sept 2026).
 * Institutional/Obondo-Advanced-trial users hitting `/chat/assistant/*`
 * already get the Obondo persona server-side (see routes/trading_assistant.py),
 * so this reuses the same endpoints as KimaniFAB's MiniChat, just embedded
 * inline instead of floating.
 *
 * Sep 2026: Brought to full parity with Kimani (AiAssistant.jsx) — proactive
 * Signals inbox, 6-language toggle, 3 output channels (text/voice/graph) and
 * 3 depths (simple/technical/enhanced), all backed by the SAME
 * /api/chat/assistant/* endpoints and persisted preferences.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Send, Sparkles, Bell, Languages, VolumeX, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import MarkdownMessage from "@/components/MarkdownMessage";
import SignalsInbox from "@/components/assistant/SignalsInbox";
import PrefGroup from "@/components/assistant/PrefGroup";
import { LANG_OPTIONS, CHANNEL_OPTIONS, DEPTH_OPTIONS, TTS_LANG_MAP, TTS_RATE_OPTIONS, getStoredTtsRate, setStoredTtsRate } from "@/pages/AiAssistant";

function stripChartBlocks(text) {
    return (text || "").replace(/```chart[\s\S]*?```/g, "").trim();
}

export default function ObondoChatPanel() {
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages]   = useState([]);
    const [input, setInput]         = useState("");
    const [busy, setBusy]           = useState(false);
    const [prefs, setPrefs]         = useState({ language: "en", channel: "text", depth: "technical" });
    const [showSignals, setShowSignals]   = useState(false);
    const [signalCount, setSignalCount]   = useState(0);
    const [speaking, setSpeaking]         = useState(false);
    const [autoPlay, setAutoPlay]         = useState(true);
    const [ttsRate, setTtsRate]           = useState(getStoredTtsRate);
    const scrollRef = useRef(null);
    const lastSpokenIdxRef = useRef(-1);

    useEffect(() => {
        Promise.all([
            api.post("/chat/assistant/session"),
            api.get("/chat/assistant/preferences"),
        ])
            .then(([sRes, pRes]) => {
                setSessionId(sRes.data.session_id);
                setPrefs(pRes.data.prefs || { language: "en", channel: "text", depth: "technical" });
            })
            .catch(() => toast.error("Could not start Obondo chat session."));
    }, []);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const { data } = await api.get("/chat/assistant/signals");
                if (alive) setSignalCount((data.signals || []).length);
            } catch { /* silent — proactive count is a nice-to-have */ }
        };
        tick();
        const id = setInterval(tick, 60_000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    // Voice output — speak Obondo's last completed reply when channel === voice
    // AND auto-play is on. Users can still manually play any message via the
    // per-message speaker button regardless of this setting.
    const speak = useCallback((text) => {
        try {
            if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
            window.speechSynthesis.cancel();
            const u = new SpeechSynthesisUtterance(stripChartBlocks(text));
            u.lang = TTS_LANG_MAP[prefs.language] || "en-GB";
            u.rate = ttsRate;
            u.pitch = 1.0;
            u.onstart = () => setSpeaking(true);
            u.onend = () => setSpeaking(false);
            u.onerror = () => setSpeaking(false);
            window.speechSynthesis.speak(u);
        } catch { /* browser blocked TTS — silent no-op */ }
    }, [prefs.language, ttsRate]);

    useEffect(() => {
        if (prefs.channel !== "voice" || !autoPlay) return;
        if (busy) return;
        const lastIdx = messages.length - 1;
        if (lastIdx < 0 || lastIdx === lastSpokenIdxRef.current) return;
        const last = messages[lastIdx];
        if (!last || last.role !== "assistant" || !last.content) return;
        lastSpokenIdxRef.current = lastIdx;
        speak(last.content);
    }, [messages, busy, prefs.channel, autoPlay, speak]);

    useEffect(() => () => {
        try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    }, []);

    async function updatePrefs(patch) {
        const next = { ...prefs, ...patch };
        setPrefs(next);
        try {
            await api.patch("/chat/assistant/preferences", patch);
        } catch {
            toast.error("Failed to save preferences.");
        }
    }

    const send = useCallback(async () => {
        const text = input.trim();
        if (!text || busy || !sessionId) return;
        setBusy(true);
        setInput("");
        setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
        try {
            const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
            const csrfToken = document.cookie.split(";").map(c => c.trim()).find(c => c.startsWith("md_csrf="))?.split("=")[1];
            const res = await fetch(`${base}/api/chat/assistant/message`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(csrfToken && { "X-CSRF-Token": decodeURIComponent(csrfToken) }),
                },
                credentials: "include",
                body: JSON.stringify({ text, session_id: sessionId, include_portfolio: true }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                let idx;
                while ((idx = buffer.indexOf("\n\n")) !== -1) {
                    const chunk = buffer.slice(0, idx);
                    buffer = buffer.slice(idx + 2);
                    if (chunk.startsWith("event: done")) return;
                    if (chunk.startsWith("event: error")) throw new Error(chunk);
                    if (chunk.startsWith("data: ")) {
                        const delta = chunk.slice(6);
                        setMessages((m) => {
                            const copy = m.slice();
                            copy[copy.length - 1] = {
                                role: "assistant",
                                content: (copy[copy.length - 1].content || "") + delta,
                            };
                            return copy;
                        });
                    }
                }
            }
        } catch {
            toast.error("Obondo stream interrupted. Try again.");
            setMessages((m) => {
                const copy = m.slice();
                const lastMsg = copy[copy.length - 1];
                if (lastMsg && lastMsg.role === "assistant" && !lastMsg.content) {
                    copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't get a response — please try again." };
                }
                return copy;
            });
        } finally {
            setBusy(false);
        }
    }, [input, busy, sessionId]);

    return (
        <div className="border border-border rounded-xl bg-card overflow-hidden mb-6" data-testid="obondo-chat-panel">
            <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border bg-primary/5">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span className="text-sm font-bold">Ask Obondo</span>
                    <span className="text-xs text-muted-foreground uppercase tracking-widest hidden sm:inline">Talk to your coach</span>
                </div>
                <button
                    onClick={() => setShowSignals((s) => !s)}
                    data-testid="obondo-signals-toggle"
                    className="shrink-0 relative inline-flex items-center gap-1.5 min-h-[36px] px-2.5 rounded-lg border border-border hover:border-primary/50 hover:bg-secondary/40 text-xs font-medium transition-colors"
                >
                    <Bell className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Signals</span>
                    {signalCount > 0 && (
                        <span
                            data-testid="obondo-signals-count"
                            className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center"
                        >
                            {signalCount > 99 ? "99+" : signalCount}
                        </span>
                    )}
                </button>
            </div>

            <div className="px-4 pt-3 flex flex-wrap gap-2 items-center" data-testid="obondo-prefs-bar">
                <PrefGroup
                    testIdPrefix="obondo-lang"
                    icon={Languages}
                    label="Language"
                    value={prefs.language}
                    options={LANG_OPTIONS}
                    onChange={(v) => updatePrefs({ language: v })}
                />
                <PrefGroup
                    testIdPrefix="obondo-channel"
                    label="Output"
                    value={prefs.channel}
                    options={CHANNEL_OPTIONS.map((o) => ({ key: o.key, label: o.label, Icon: o.Icon }))}
                    onChange={(v) => updatePrefs({ channel: v })}
                />
                <PrefGroup
                    testIdPrefix="obondo-depth"
                    label="Depth"
                    value={prefs.depth || "technical"}
                    options={DEPTH_OPTIONS}
                    onChange={(v) => updatePrefs({ depth: v })}
                />
                {prefs.channel === "voice" && (
                    <>
                        <label className="text-xs px-2.5 py-1.5 rounded-md border border-border inline-flex items-center gap-1.5 cursor-pointer select-none" data-testid="obondo-autoplay-toggle">
                            <input
                                type="checkbox"
                                checked={autoPlay}
                                onChange={(e) => setAutoPlay(e.target.checked)}
                                className="accent-primary"
                            />
                            Auto-play replies
                        </label>
                        <label className="text-xs px-2.5 py-1.5 rounded-md border border-border inline-flex items-center gap-1.5" data-testid="obondo-tts-rate-control">
                            Speed
                            <select
                                value={ttsRate}
                                onChange={(e) => { const r = parseFloat(e.target.value); setTtsRate(r); setStoredTtsRate(r); }}
                                className="bg-transparent text-xs font-semibold focus:outline-none cursor-pointer"
                                data-testid="obondo-tts-rate-select"
                            >
                                {TTS_RATE_OPTIONS.map((r) => (
                                    <option key={r} value={r}>{r}x</option>
                                ))}
                            </select>
                        </label>
                        {speaking && (
                            <span
                                className="text-xs px-2.5 py-1.5 rounded-md border border-primary/40 bg-primary/10 text-primary inline-flex items-center gap-1.5 font-semibold"
                                data-testid="obondo-voice-speaking-indicator"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                Speaking…
                            </span>
                        )}
                        <button
                            onClick={() => { try { window.speechSynthesis.cancel(); } catch { /* noop */ } setSpeaking(false); }}
                            className="text-xs px-2.5 py-1.5 rounded-md border border-border hover:border-primary/50 inline-flex items-center gap-1.5"
                            data-testid="obondo-voice-stop"
                        >
                            <VolumeX className="w-3.5 h-3.5" /> Stop speaking
                        </button>
                    </>
                )}
            </div>

            {showSignals && (
                <div className="px-4 pt-3">
                    <SignalsInbox
                        onClose={() => setShowSignals(false)}
                        onScanned={(n) => setSignalCount(n)}
                    />
                </div>
            )}

            <div ref={scrollRef} className="max-h-64 overflow-y-auto p-4 space-y-3" data-testid="obondo-chat-messages">
                {messages.length === 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {["What should I know before my first trade?", "How am I trading this week?", "Explain reward-to-risk"].map((s) => (
                            <button
                                key={s}
                                onClick={() => setInput(s)}
                                className="text-xs px-2.5 py-1 rounded-full border border-border/60 hover:border-primary/50 hover:text-primary transition-colors"
                                data-testid={`obondo-chat-suggestion-${s.slice(0, 12).replace(/\W+/g, "-").toLowerCase()}`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                )}
                {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div
                            className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed relative group ${
                                m.role === "user" ? "bg-primary text-primary-foreground whitespace-pre-wrap" : "bg-secondary/60 text-foreground"
                            }`}
                            data-testid={`obondo-chat-msg-${m.role}-${i}`}
                        >
                            {m.role !== "user" && m.content && (
                                <button
                                    onClick={() => speak(m.content)}
                                    title="Play this reply aloud"
                                    data-testid="obondo-message-speak-btn"
                                    className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-card border border-border text-muted-foreground hover:text-primary hover:border-primary/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <Volume2 className="w-2.5 h-2.5" />
                                </button>
                            )}
                            {m.role === "user" ? m.content : (
                                m.content ? <MarkdownMessage text={m.content} /> : (
                                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                        Thinking…
                                    </span>
                                )
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="border-t border-border p-2.5 flex gap-1.5">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Ask Obondo anything…"
                    className="flex-1 h-9 rounded-md border border-border bg-background px-3 text-xs focus:outline-none focus:border-primary"
                    data-testid="obondo-chat-input"
                    disabled={busy}
                />
                <button
                    onClick={send}
                    disabled={busy || !input.trim()}
                    className="h-9 w-9 rounded-md flex items-center justify-center text-primary-foreground bg-primary disabled:opacity-40 transition-opacity"
                    data-testid="obondo-chat-send"
                >
                    <Send className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}
